import { HttpInterceptorFn } from '@angular/common/http';
import { from, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';

const API_CLIENT_ID = environment.apiClientId;
const API_CLIENT_SECRET = environment.apiClientSecret;

export const apiSignatureInterceptor: HttpInterceptorFn = (req, next) => {
  if (!shouldSignRequest(req.url)) {
    return next(req);
  }

  return from(buildSignatureHeaders(req.method, req.urlWithParams)).pipe(
    switchMap(headers => next(req.clone({ setHeaders: headers })))
  );
};

function shouldSignRequest(url: string): boolean {
  if (!API_CLIENT_ID || !API_CLIENT_SECRET) {
    return false;
  }

  const apiBase = new URL('api/', environment.apiURL).toString();
  const requestUrl = new URL(url, window.location.origin).toString();

  return requestUrl.startsWith(apiBase);
}

async function buildSignatureHeaders(method: string, requestUrl: string): Promise<Record<string, string>> {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const nonce = createNonce();
  const url = new URL(requestUrl, window.location.origin);
  const target = `${url.pathname}${url.search}`;

  const payload = [
    method.toUpperCase(),
    target,
    API_CLIENT_ID,
    timestamp,
    nonce,
  ].join('\n');

  return {
    'X-API-CLIENT': API_CLIENT_ID,
    'X-API-TIMESTAMP': timestamp,
    'X-API-NONCE': nonce,
    'X-API-SIGNATURE': await hmacSha256Base64(payload, API_CLIENT_SECRET),
  };
}

async function hmacSha256Base64(payload: string, secret: string): Promise<string> {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(payload));

  return bytesToBase64(new Uint8Array(signature));
}

function createNonce(): string {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
}
